/** @file One paint's data from tracks, selection, view state and size: maps, frame-all, scene, grid. */
import { HANDLE_PX } from '@pose2d/editor-geometry/handlePx';
import { timelineScale } from '@pose2d/editor-geometry/timelineScale';
import type { KeyRef } from '../../stores/types';
import { buildCurveScene } from './buildCurveScene';
import { CURVE_GRAPH } from './curveGraph.data';
import { curveHitGrid, type CurveHitGrid } from './curveHitGrid';
import { curveMaps } from './curveMaps';
import type { CurveAxes, CurvePreview, CurveRef, CurveScene, CurveTrack, CurveTrackMap } from './curveTypes';
import { curveValueScale } from './curveValueScale';
import type { CurveViewState } from './curveViewStore';
import { toCurveRef } from './toCurveRef';

/** What a paint draws and what the pointer machine picks against; `fitted` says whether axes were fitted. */
export interface CurvePrep {
  axes: CurveAxes;
  fitted: boolean;
  maps: CurveTrackMap[];
  scene: CurveScene;
  grid: CurveHitGrid;
}

const sameRef = (a: CurveRef, b: CurveRef): boolean => a.animation === b.animation && a.channel === b.channel && a.target === b.target;

/** Axes showing every key and the sampled curves, below the ruler strip, padded by CURVE_GRAPH.fitPadPx. */
function frameAll(tracks: readonly CurveTrack[], maps: readonly CurveTrackMap[], width: number, height: number): CurveAxes {
  const [first, last] = curveMaps.frames(tracks);
  const [lo, hi] = curveMaps.bounds(tracks, maps);
  return {
    time: timelineScale.fit(first, last, width, CURVE_GRAPH.fitPadPx),
    value: curveValueScale.fit(lo, hi, CURVE_GRAPH.rulerPx, height, CURVE_GRAPH.fitPadPx),
  };
}

/** Per track, the frames of its selected keys (KeyRef channels normalized through toCurveRef). */
function selectedFrames(tracks: readonly CurveTrack[], keys: readonly KeyRef[]): Set<number>[] {
  const picked = keys.flatMap((key) => {
    const ref = toCurveRef(key);
    return ref === null ? [] : [{ ref, frame: key.frame }];
  });
  return tracks.map((track) => new Set(picked.filter((p) => sameRef(p.ref, track.ref)).map((p) => p.frame)));
}

function previewOf(view: CurveViewState): CurvePreview | null {
  const g = view.gesture;
  return g.kind === 'handle' && g.curve !== null ? { track: g.track, segment: g.segment, curve: g.curve } : null;
}

/** Fits the axes once tracks exist and the view is not fitted; otherwise keeps the view's axes. */
export function prepareCurveFrame(tracks: readonly CurveTrack[], keys: readonly KeyRef[], view: CurveViewState, size: { width: number; height: number }): CurvePrep {
  const maps = tracks.map((track) => curveMaps.of(track, view.normalized));
  const fit = !view.fitted && tracks.length > 0;
  const axes = fit ? frameAll(tracks, maps, size.width, size.height) : { time: view.time, value: view.value };
  const scene = buildCurveScene({ tracks, maps, ...axes, width: size.width, selected: selectedFrames(tracks, keys), preview: previewOf(view) });
  return { axes, fitted: view.fitted || fit, maps, scene, grid: curveHitGrid.build(scene.points, size.width, size.height, HANDLE_PX.point) };
}
